
import React, { useEffect, useState } from 'react';
import { usePieceStore } from '../../stores/usePieceStore';
import { User, Calendar } from 'lucide-react';

interface Props {
  onSelect: (tailorId: string, tailorName: string, startDate?: string) => void;
  onCancel: () => void;
}

export const TailorSelector: React.FC<Props> = ({ onSelect, onCancel }) => {
  const { tailors, fetchTailors, loading } = usePieceStore();

  // Initialize to current local time (ISO format sliced for input)
  // Initialize to current local time (ISO format sliced for input)
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  });
  const [selectedTailorId, setSelectedTailorId] = useState<string | null>(null);

  useEffect(() => { fetchTailors(); }, []);

  const handleConfirm = () => {
    if (!selectedTailorId) return;
    const tailor = tailors.find(t => t.id === selectedTailorId);
    if (tailor) {
      onSelect(tailor.id, tailor.full_name || 'Unknown', new Date(startDate).toISOString());
    }
  };

  return (
    <div className="fixed inset-0 bg-black/25 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4 animate-overlay">
      <div className="bg-white border border-slate-100 rounded-2xl w-full max-w-sm p-5 shadow-2xl animate-modal">
        <h3 className="text-lg font-bold text-slate-800 mb-1">Sewing Load</h3>
        <p className="text-xs text-slate-400 font-medium mb-4">Assign piece and start tracking time.</p>

        {/* Date Selection */}
        <div className="mb-4 bg-slate-50 p-3 rounded-xl border border-slate-100">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-2 flex items-center gap-1">
            <Calendar size={10} /> Sewing Start Date
          </label>
          <input
            type="datetime-local"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 font-mono"
          />
        </div>

        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-2">Select Tailor</label>
        <div className="max-h-60 overflow-y-auto space-y-1.5 mb-6">
          {tailors.length === 0 && !loading && (
            <p className="text-slate-400 text-center py-6 text-xs font-medium">No tailors in system.</p>
          )}

          {tailors.map((tailor, i) => {
            const isSelected = selectedTailorId === tailor.id;
            return (
              <button
                key={tailor.id}
                onClick={() => setSelectedTailorId(tailor.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left animate-card ${isSelected ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-500' : 'bg-white border-slate-100 hover:bg-slate-50'}`}
                style={{ animationDelay: `${i * 30}ms` }}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
                  <User size={14} />
                </div>
                <span className={`font-semibold text-sm ${isSelected ? 'text-blue-700' : 'text-slate-700'}`}>{tailor.full_name}</span>
              </button>
            )
          })}
        </div>

        <button
          onClick={handleConfirm}
          disabled={!selectedTailorId}
          className="w-full py-3.5 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-600/20 active:scale-95 transition-transform disabled:opacity-50 disabled:scale-100 mb-3"
        >
          Start Sewing
        </button>

        <button
          onClick={onCancel}
          className="w-full py-3 text-slate-500 font-semibold text-sm bg-slate-50 rounded-xl active:bg-slate-100 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};